import type { MaterialEntity, MaterialEntityCreate, MaterialEntityWithMemos } from './types';
import { createAuthenticatedFetch, type AssetExistsResponse } from './types/core/common';

// Interface pour les erreurs API
interface ApiError {
  status?: number;
  message?: string;
}

// @deprecated Use imports from '@/api/types' instead
export type {
    MaterialEntity,
    MaterialEntityCreate,
    MaterialEntityWithMemos
};

const api = createAuthenticatedFetch();

export const materialEntities = {
  // ===============================
  // Méthodes Legacy (compatibilité)
  // ===============================
  
  async fetchAllMaterialEntities(): Promise<MaterialEntity[]> {
    try {
      return await api.get('/materialentity/admin/all');
    } catch (error) {
      throw new Error("Erreur lors de la récupération des entités matérielles");
    }
  },

  // ===============================
  // Nouvelles Méthodes Sécurisées
  // ===============================

  /**
   * Récupère les entités matérielles de l'utilisateur connecté
   */
  async getMy(): Promise<MaterialEntity[]> {
    return api.get<MaterialEntity[]>('/materialentity/my');
  },

  /**
   * Récupère les entités matérielles publiques seulement
   */
  async getPublic(): Promise<MaterialEntity[]> {
    return api.get<MaterialEntity[]>('/materialentity/public');
  },

  /**
   * Récupère les entités matérielles accessibles (utilisateur + publiques)
   */
  async getAccessible(): Promise<MaterialEntity[]> {
    return api.get<MaterialEntity[]>('/materialentity/accessible');
  },

  /**
   * Récupère toutes les entités matérielles (admin seulement)
   */
  async getAllAdmin(): Promise<MaterialEntity[]> {
    return api.get<MaterialEntity[]>('/materialentity/all');
  },

  /**
   * Récupère toutes les entités matérielles (admin seulement), y compris soft-deleted
   */
  async getAllAdminWithDeleted(): Promise<MaterialEntity[]> {
    return api.get<MaterialEntity[]>('/materialentity/all_with_deleted');
  },

  // ===============================
  // Méthodes CRUD Sécurisées
  // ===============================

  async createMaterialEntity(entity: MaterialEntityCreate): Promise<MaterialEntity> {
    return api.post<MaterialEntity>('/materialentity/', entity);
  },

  async updateMaterialEntity(uuid: string, entity: Partial<MaterialEntityCreate>): Promise<MaterialEntity> {
    return api.put<MaterialEntity>(`/materialentity/${uuid}`, entity);
  },

  async deleteMaterialEntity(uuid: string): Promise<void> {
    return api.delete(`/materialentity/${uuid}`);
  },

  async hardDeleteMaterialEntity(uuid: string): Promise<void> {
    return api.delete(`/materialentity/hard_delete/${uuid}`);
  },

  async restoreMaterialEntity(uuid: string): Promise<void> {
    return api.put(`/materialentity/restore/${uuid}`);
  },

  async fetchMaterialEntityWithMemos(uuid: string): Promise<MaterialEntityWithMemos> {
    try {
      return await api.get(`/materialentity/${uuid}/with-memos`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Entité matérielle non trouvée");
      }
      throw new Error("Erreur lors de la récupération de l'entité matérielle et ses mémos");
    }
  },

  async fetchUserMaterialEntities(userId: string): Promise<MaterialEntityWithMemos[]> {
    try {
      return await api.get(`/materialentity/user/${userId}`);
    } catch (error) {
      throw new Error("Erreur lors de la récupération des entités matérielles de l'utilisateur");
    }
  },

  /**
   * Récupère une entité matérielle publique par UUID (accès public)
   */
  async getPublicByUuid(uuid: string): Promise<MaterialEntity> {
    try {
      return await api.get(`/materialentity/public/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Entité matérielle publique non trouvée");
      }
      throw new Error("Erreur lors de la récupération de l'entité matérielle publique");
    }
  },

  /**
   * Récupère une entité matérielle par UUID avec ses mémos (accès public ou par connaissance de l'UUID)
   */
  async getPublicByUuidWithMemos(uuid: string): Promise<MaterialEntityWithMemos> {
    try {
      return await api.get(`/materialentity/public/${uuid}/with-memos`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Entité matérielle non trouvée");
      }
      throw new Error("Erreur lors de la récupération de l'entité matérielle et ses mémos");
    }
  },

  /**
   * Vérifie si une entité matérielle existe par son UUID (public ou privé)
   */
  async checkExists(uuid: string): Promise<AssetExistsResponse> {
    try {
      return await api.get(`/materialentity/exists/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        return {
          exists: false,
          is_owner: false,
          is_public: false
        };
      }
      throw new Error("Erreur lors de la vérification de l'existence de l'entité matérielle");
    }
  }
};

// Exports des fonctions individuelles pour la rétrocompatibilité
export const fetchAllMaterialEntities = (): Promise<MaterialEntity[]> => materialEntities.fetchAllMaterialEntities();
export const createMaterialEntity = (entity: MaterialEntityCreate): Promise<MaterialEntity> => materialEntities.createMaterialEntity(entity);
export const updateMaterialEntity = (uuid: string, entity: Partial<MaterialEntityCreate>): Promise<MaterialEntity> => materialEntities.updateMaterialEntity(uuid, entity);
export const deleteMaterialEntity = (uuid: string): Promise<void> => materialEntities.deleteMaterialEntity(uuid);
export const fetchMaterialEntityWithMemos = (uuid: string): Promise<MaterialEntityWithMemos> => materialEntities.fetchMaterialEntityWithMemos(uuid);
export const fetchUserMaterialEntities = (userId: string): Promise<MaterialEntity[]> => materialEntities.fetchUserMaterialEntities(userId); 